import { RWA_COIN_ICON } from "~/consts";
import { ImageLoader } from "~/components/ImageLoader";
import { Link } from "~/components/Link";
import {
    AccessSegment,
    ColumnMetadata,
    ColumnTypes,
} from "~/components/DataGrid";
import { Icon } from "~/components/Icon";
import { NetworkBadge } from "~/modules/assets/components/AssetTable/NetworkBadge";
import { GlobalAssetTableEvents } from "~/modules/analytics/events/global_asset_table";
import { Asset, AssetColumns, Network } from "~/modules/assets/types";
import { getNetworkURL, getPlatformURL } from "~/routes";
import { getAssetURL } from "~/utils/getAssetURL";
import { getDominantPlatform } from "~/utils/getDominantPlatform";
import { getMeasureValue } from "~/utils/getMeasureValue";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";

// // // //

// NOTE - sized for 1-3 digit ranks inside the cell padding; ranks are
// right-aligned, so 4 digit ranks (1,000+) grow into the left padding
const RANK_COLUMN_WIDTH = 64;

/**
 * Unique networks the asset has tokens on, sorted by name (excluding book-entry)
 */
function getAssetNetworks(asset: Asset): Network[] {
    const networkSlugs = uniqueValues(
        asset.tokens.map((token) => token.network.slug),
    ).filter((slug) => slug !== "book-entry");

    const networks = networkSlugs
        .map((slug) => asset.tokens.find((t) => t.network.slug === slug))
        .filter((token) => token !== undefined)
        .map((token) => token.network);

    return sortItemsByProperty({
        items: networks,
        field: "name",
        direction: "asc",
    });
}

// // // //

/**
 * Rank column - ranks follow the order of the loaded rows (i.e. the API's
 * sort), so they're looked up by asset id
 */
export function getRankColumn(
    rankByAssetID: Map<number, number>,
): ColumnMetadata<Asset, AssetColumns> {
    return {
        label: "#",
        slug: "rank",
        type: ColumnTypes.number,
        width: RANK_COLUMN_WIDTH,
        maxWidth: RANK_COLUMN_WIDTH,
        sticky: {
            left: 0,
            isLast: false,
        },
        cellValue: (asset) => rankByAssetID.get(asset.id) ?? null,
    };
}

export const NAME_COLUMN: ColumnMetadata<Asset, AssetColumns> = {
    label: "Name",
    slug: "name",
    sortsOn: AssetColumns.name,
    type: ColumnTypes.string,
    width: 350,
    sticky: {
        left: RANK_COLUMN_WIDTH,
        isLast: true,
    },
    render: (asset) => {
        return (
            <Link
                href={getAssetURL({ asset })}
                variant="default"
                className="flex items-center gap-1.5"
                analyticsLabel={GlobalAssetTableEvents.ClickAssetLink}
                analyticsEventProperties={{
                    asset_ticker: asset.ticker,
                }}
            >
                <ImageLoader
                    src={asset.icon_url || RWA_COIN_ICON}
                    alt={`${asset.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-6 w-6 min-w-6 rounded-full"
                />
                <div className="flex flex-col dg-overflow ml-2">
                    <div className="dg-overflow">{asset.name}</div>
                    {asset.ticker && (
                        <div className="text-sm text-slate-500">
                            {asset.ticker}
                        </div>
                    )}
                </div>
            </Link>
        );
    },
};

export const VALUE_COLUMN: ColumnMetadata<Asset, AssetColumns> = {
    slug: "value",
    label: "Total Value",
    type: ColumnTypes.dollar,
    sortsOn: AssetColumns.bridgedTokenValueDollar,
    width: 160,
    maxWidth: 160,
    cellValue: (asset) => {
        return (
            getMeasureValue(asset, "bridged_token_value_dollar")?.val ?? null
        );
    },
};

export const APY_30D_COLUMN: ColumnMetadata<Asset, AssetColumns> = {
    slug: "apy_30d",
    label: "30D APY",
    type: ColumnTypes.percent,
    sortsOn: AssetColumns.apy30Day,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    width: 112,
    maxWidth: 112,
    cellValue: (asset) => {
        // NOTE - apy_30_day is a percentage, the percent column expects a decimal
        const apy = getMeasureValue(asset, "apy_30_day");
        return apy ? apy.val / 100 : null;
    },
};

export const PCT_CHANGE_30D_COLUMN: ColumnMetadata<Asset, AssetColumns> = {
    slug: "pct_change_30d",
    label: "30D",
    type: ColumnTypes.percentChange,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    width: 100,
    maxWidth: 100,
    cellValue: (asset) => {
        // NOTE - getMeasureValue returns chg_30d_pct as a decimal
        return (
            getMeasureValue(asset, "bridged_token_value_dollar")?.chg_30d_pct ??
            null
        );
    },
};

export const PROTOCOL_COLUMN: ColumnMetadata<Asset, AssetColumns> = {
    label: "Platform",
    slug: "protocol",
    type: ColumnTypes.string,
    width: 170,
    render: (asset) => {
        const platform = getDominantPlatform(asset.tokens);

        if (!platform) {
            return null;
        }

        return (
            <Link
                href={getPlatformURL({ platformSlug: platform.slug })}
                analyticsLabel={GlobalAssetTableEvents.ClickProtocolLink}
                analyticsEventProperties={{
                    protocol: platform.name,
                }}
                className="flex items-center gap-2"
                variant="default"
            >
                <ImageLoader
                    src={platform.icon_url || RWA_COIN_ICON}
                    alt={`${platform.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-5 w-5 min-w-5 rounded-full"
                />
                <div className="dg-overflow">{platform.name}</div>
            </Link>
        );
    },
};

export const ASSET_CLASS_COLUMN: ColumnMetadata<Asset, AssetColumns> = {
    slug: "asset_class",
    label: "Asset Class",
    type: ColumnTypes.string,
    width: 200,
    maxWidth: 250,
    render: (asset) => {
        const assetClass = getAssetClassLinkMetadata(asset.asset_class_id);

        if (assetClass.label === null) {
            return null;
        }

        if (!assetClass.href) {
            return (
                <div className="flex items-center gap-2 text-secondary-foreground overflow-hidden">
                    <Icon className="h-3.5" icon={assetClass.icon} />
                    <span className="dg-overflow">{assetClass.label}</span>
                </div>
            );
        }

        return (
            <Link
                href={assetClass.href}
                analyticsLabel={GlobalAssetTableEvents.ClickAssetClassLink}
                analyticsEventProperties={{
                    asset_class: assetClass.label,
                }}
                variant="default"
                className="flex items-center gap-2 overflow-hidden"
            >
                <Icon className="h-3.5" icon={assetClass.icon} />
                <span className="dg-overflow">{assetClass.label}</span>
            </Link>
        );
    },
};

export const NETWORKS_COLUMN: ColumnMetadata<Asset, AssetColumns> = {
    slug: "networks",
    label: "Networks",
    type: ColumnTypes.string,
    width: 180,
    maxWidth: 225,
    render: (asset) => {
        const allNetworks = getAssetNetworks(asset);

        if (allNetworks.length === 1) {
            const network = allNetworks[0];

            return (
                <Link
                    variant="default"
                    className="py-0.5 px-0.5 flex items-center"
                    analyticsLabel={GlobalAssetTableEvents.ClickNetworkLink}
                    analyticsEventProperties={{
                        network: network.slug,
                    }}
                    href={getNetworkURL({ networkSlug: network.slug })}
                >
                    <NetworkBadge
                        network={{
                            slug: network.slug,
                            name: network.name,
                            icon_url: network.icon_url || RWA_COIN_ICON,
                        }}
                        enableTooltip={false}
                    />
                    <p className="ml-2 dg-overflow">{network.name}</p>
                </Link>
            );
        }

        return (
            <div className="py-0.5 px-0.5 isolate flex -space-x-2 overflow-hidden h-full items-center w-full">
                {allNetworks.map((n) => (
                    <Link
                        key={n.slug}
                        href={getNetworkURL({ networkSlug: n.slug })}
                        analyticsLabel={GlobalAssetTableEvents.ClickNetworkLink}
                        analyticsEventProperties={{
                            network: n.slug,
                        }}
                    >
                        <NetworkBadge
                            network={{
                                slug: n.slug,
                                name: n.name,
                                icon_url: n.icon_url || RWA_COIN_ICON,
                            }}
                        />
                    </Link>
                ))}
            </div>
        );
    },
};
